import {
    ChangeDetectionStrategy, Component, computed, DestroyRef,
    effect,
    HostListener, inject, Injector, OnDestroy, signal, untracked, viewChild, WritableSignal
} from '@angular/core';
import type { AgGridAngular } from 'ag-grid-angular';
import {
    _SortModule,
    AgColumn,
    CellKeyDownEvent,
    CellSelectionChangedEvent,
    ColDef,
    ColumnHeaderClickedEvent,
    ColumnMovedEvent,
    ColumnResizedEvent,
    DefaultMenuItem,
    FirstDataRenderedEvent,
    GetContextMenuItems,
    GetContextMenuItemsParams,
    GetMainMenuItems,
    GetMainMenuItemsParams,
    GridApi,
    GridOptions,
    GridState,
    IRowNode,
    MenuItemDef,
    RowSelectedEvent,
    ScrollState,
    SelectionChangedEvent,
    SortChangedEvent,
    SortService,
    ValueGetterFunc
} from 'ag-grid-enterprise';
import { DataAggregation } from 'src/app/pbir/dataModel';
import { debounced } from 'src/app/pbir/utils/debounced';
import { logger } from 'src/app/pbir/utils/logger';
import { SortDirection } from 'src/app/services/api.services';
import { AppError, isNullOrUndefined } from 'src/app/shared/utils';
import { VisualComponent } from '../../base/Visual.component';
import { RowsResult } from '../../data';
import { VisualDataAdapter } from '../../data/VisualDataAdapter';
import { Visual } from '../../Visual';
import { Serialize } from '../../VisualDataSerializer';
import { VisualField } from '../../VisualField';
import { VisualFieldRoleType } from '../../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../../VisualFieldRoleTypeMap';
import { VisualFieldSorting } from '../../VisualFieldSorting';
import { VisualHost } from '../../VisualHost';
import { DataPoint } from '../../VisualInteractions';
import { VisualStyle } from '../../VisualStyle';
import { VisualType } from '../../VisualType';
import * as AgGridConstants from '../shared';
import { AgGridTableStyleEditorComponent } from '../styleEditor/TableStyleEditorComponent';
import { AgGridTableVisualDataRow, AgGridTableVisualDataSource } from './dataSource';
import { tap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppContext } from '../../../../app/context';

const LOGGER = logger('AgGridTableVisualComponent');

// HACK: Override default sort index update function
// There does not seem to be a diff way of ensuring that multi-sort does not change order
// TODO: Change this to work with both PivotTable and Table
let sortSvcPrototype = (_SortModule.beans[0] as any).prototype;
let originalUpdateSortIndex = sortSvcPrototype.updateSortIndex;
sortSvcPrototype.updateSortIndex = function (this: SortService, lastColToChange: AgColumn) {
  // update sortIndex on all sorting cols
  const allSortedCols = this.getColumnsWithSortingOrdered();
  this.beans.colModel.forAllCols(x => this.setColSortIndex(x, null));

  let sortIndex = 0;
  let unsort = !lastColToChange.getSort();

  allSortedCols.forEach(col => {
    if (unsort && col == lastColToChange)
      return;

    this.setColSortIndex(col, sortIndex);
    sortIndex++;
  });

  if (unsort)
    this.setColSortIndex(lastColToChange, null);
};


@Component({
  selector: 'pbir-visual__ag-grid__table',
  templateUrl: './TableVisual.component.html',
  styleUrls: ['../styles.scss', './TableVisual.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[style.--ag-grid-table-header-height]': 'tableLayout().headerHeightPx',
    '[style.--ag-grid-table-row-height]': 'tableLayout().rowHeightPx',
    '[style.--ag-grid-table-grand-total-row-height]': 'tableLayout().grandTotalRowHeightPx',

    '[style.--ag-grid-sort-indicator-font-size]': 'tableLayout().sortIndicatorFontSize',
    '[style.--ag-grid-sort-indicator-size]': 'tableLayout().sortIndicatorSizePx',
  }
})
export class AgGridTableVisualComponent 
  extends VisualComponent<
    AgGridTableVisual.Options, 
    AgGridTableVisual.State,
    AgGridTableVisual.Style,
    VisualComponent.DefaultData> {
  readonly AgGridConstants = AgGridConstants;

  readonly injector   = inject(Injector);
  readonly destroyRef = inject(DestroyRef);

  readonly tableLayout = computed(() => this.style()?.tableLayout() ?? AgGridConstants.TableLayout.standard);

  readonly appContext = inject(AppContext);
  readonly features = this.appContext.features;

  /** 
   * Visual field roles.
   * Table only supports 'columns', which consists of both groupings and measures.
   */
  readonly roles = computed(() => {
    return {
      columns: this.visual()?.fieldRoles.find(AgGridTableVisual.roles.columns)
    };
  });

  readonly tableFields       = signal<ReadonlyArray<TableField>>([]);
  readonly tableFieldsUpdate = effect(() => {
    let requestIndex = this.data.requestIndex();
    let visualFields = [...this.roles().columns?.fields.exludingInvalidCalc() ?? []];
    
    untracked(() => {
      let tableFields = this.tableFields();
      let anyNewFields = false;

      let newFields = visualFields.map((x, i) => {
        let tf = tableFields.find(f => f.vf.target.equals(x.target));
        if (tf && tf.requestIndex == requestIndex)
          return tf;

        anyNewFields = true;
        return new TableField(this, x, requestIndex, i);
      });

      if (!anyNewFields) {
        newFields.forEach((x, i) => x.index.set(i));
      }
      else {
        this.tableFields.set(newFields);
        this.reset();
      }

      return newFields;
    });
  });

  readonly orderedTableFields = computed(() => [...this.tableFields()].sort((a, b) => a.index() - b.index()));


  private reset() {
    if (!this.resetAgGrid()) {
      this.resetAgGrid.set(true);
      this.dataTable.data.updateSource();

      this.dataTable.ag.onGridReset();
      this.grandTotalsTable.ag.onGridReset();
    }

    this.unsetResetAgGridDebounced();
  }
  
  readonly resetAgGrid = signal(false);

  private readonly unsetResetAgGridDebounced = debounced(() => {
    this.resetAgGrid.set(false);
  }, 200, this.destroyRef);


  readonly canRender = computed(() => this.isVisualValid() && this.tableFields().length > 0);
  
  readonly cellNavigationStarted = signal(false);
  
  //#region Selection
  readonly clearSelection_WhenFieldsChange = effect(() => {
    let _ = [this.orderedTableFields()];
    this.selectionClear();
  });

  protected override onSelectionChange(e) {
    super.onSelectionChange(e);
  }

  protected override onSelectionCleared(e) {
    untracked(() => {
      super.onSelectionCleared(e);

      let ag = this.dataTableAgGrid()?.api;
      if (ag && !ag.isDestroyed()) {
        ag.deselectAll();
        ag.clearCellSelection();
      }

      this.cellNavigationStarted.set(false);
    });
  }
  //#endregion
  
  //#region Sorting
  updateFieldSort(field: TableField, options: { direction: SortDirection | null, userSort: boolean, append?: boolean }) {
    untracked(() => {
      let v = this.visual();
      if (!v)
        return;

      let fieldSorting = v.fieldSorting();

      let { direction, userSort, append } = options;

      if (direction) {
        if (userSort) {
          if (this.defaultSortApplied()) {
            this.defaultSortApplied.set(false);
            fieldSorting.unsetAllSort();
          }
        }

        if (!append)
          fieldSorting.unsetAllSort();

        let sortOrder =
          field.vf.sorted()
            ? field.vf.sortOrder()
            : this.getNextSortOrder();

        fieldSorting.setSortingOf(field.vf, direction, sortOrder);
      }
      else {
        fieldSorting.setSortingOf(field.vf, null);
        this.applyDefaultFieldSortIfNecessary();
      }
    });
  }

  removeFieldSort(field: TableField) {
    this.updateFieldSort(field, { direction: null, userSort: false });
  }

  clearFieldSort() {
    untracked(() => {
      let v = this.visual();
      if (!v)
        return;
        
      let fieldSorting = v.fieldSorting();
      fieldSorting.unsetAllSort();
    });
  }


  readonly defaultSortApplied = signal(false);

  applyDefaultFieldSort() {
    untracked(() => {
      let v = this.visual();
      if (!v)
        return;

      let tableFields = this.orderedTableFields();
      if (tableFields.length > 0) {
        let fieldSorting = v.fieldSorting();
        fieldSorting.unsetAllSort();

        let firstField = tableFields[0];
        fieldSorting.setSortingOf(firstField.vf, firstField.vf.defaultSortDirection());

        this.defaultSortApplied.set(true);
      }
    });
  }

  /**
   * Applies default field sort under the following conditions:
   * - There is more than one field present in the table.
   * - There is no sorting applied.
   * - There is default sort applied, but field order changed.
   **/
  private applyDefaultFieldSortIfNecessary() {
    untracked(() => {
      let v = this.visual();
      if (!v)
        return;
        
      let fieldSorting = v.fieldSorting();
      if (fieldSorting.sortedFieldCount() != 0) {
        if (!this.defaultSortApplied()) {
          // Default sort is not applied - meaning user has sorted the visual.
          return;
        }

        let sortList = fieldSorting.getOrderedSortList();
        if (sortList[0] == this.orderedTableFields()[0].vf) {
          // First field is sorted, which is what default sort is.
          return;
        }
      }

      this.applyDefaultFieldSort();
    });
  }

  private applyDefaultFieldSortIfNecessary_tableFieldsChange = effect(() => {
    let _ = [this.orderedTableFields(), this.defaultSortApplied()];
    this.applyDefaultFieldSortIfNecessary();
  });
  //#endregion

  //#region AgGrid
  readonly dataTableAgGrid        = viewChild<AgGridAngular>('agDataTable');
  readonly grandTotalsTableAgGrid = viewChild<AgGridAngular>('agGrandTotalsTable');

  onAgGridColumnHeaderClicked(e: ColumnHeaderClickedEvent) {
  }

  onAgGridSortChanged(e: SortChangedEvent) {
    e.api.clearCellSelection();

    if (e.source != 'uiColumnSorted') 
      return;

    this.applySortFromAgGrid();
  }

  private applySortFromAgGrid() {
    let api = this.dataTableAgGrid()?.api;
    if (!api || api.isDestroyed())
      return;

    api.getAllDisplayedColumns().forEach((c, i) => {
      let field = <TableField>c.getUserProvidedColDef().context;
      if (!field) {
        return;
      }

      let sortState = c.getSort();
      let sortDirection =
        sortState == 'asc' ? SortDirection.Ascending :
          sortState == 'desc' ? SortDirection.Descending : null;

      if (sortDirection)
        this.updateFieldSort(field, { direction: sortDirection, userSort: true, append: true });
      else
        this.removeFieldSort(field);
    });

    this.broadcastStateChange();
  }


  onAgGridColumnResized(e: ColumnResizedEvent) {
    LOGGER.debug('columnResized', e);

    if (e.source === 'uiColumnResized') {
      let field = e.column?.getColDef()?.context as TableField;
      field.userResized.set(true);
      this.captureAndSetColumnsState();
    }
  }

  onAgGridColumnMoved(e: ColumnMovedEvent) {
    LOGGER.debug('columnMoved', e);

    if (!e.finished)
      return;

    let visualFields = this.roles()?.columns.fields;
    if (!visualFields)
      return;

    if (!e.column)
      return;

    let movedField = (e.column.getUserProvidedColDef().context as TableField)?.vf;
    if (!movedField) {
      LOGGER.warn(`
        Got column moved changed event for column '${e.column.getColId()}', 
        but it does not have a TableField reference assigned.`, e);

      return;
    }

    let order = e.api.getAllDisplayedColumns().map(x => (x.getUserProvidedColDef().context as TableField)?.vf);
    
    visualFields.sort((a, b) => {
      let ai = order.findIndex(x => x.target.equals(a.target)) ?? 9999;
      let bi = order.findIndex(x => x.target.equals(b.target)) ?? 9999;

      return ai - bi;
    });

    this.broadcastStateChange();
  }


  private hasAnySelection() {
    let api = this.dataTable.ag.grid().api;
    return api.getCellRanges().length > 0;
  }

  private getSelectedRows() {
    let api = this.dataTable.ag.grid().api;
      
    let selectedCellRanges = api.getCellRanges() ?? [];

    let rows: AgGridTableVisualDataRow[] = [];

    for (let x of selectedCellRanges) {
      let min = x.startRow.rowIndex;
      let max = x.endRow.rowIndex;
      if (min > max)
        [max, min] = [min, max];

      for (let i = min; i <= max; ++i) {
        let row = api.getRowNode('' + i);
        if (row?.data)
          rows.push(row.data as AgGridTableVisualDataRow);
      }
    }

    if (!rows.length)
      return api.getSelectedRows() as AgGridTableVisualDataRow[];

    return rows;
  }

  private updateDataPointSelectionFromAgGrid() {
    untracked(() => {
      if (!this.dataTable.ag.gridReady())
        return;

      let api = this.dataTable.ag.grid().api;
      let dataPoints: DataPoint[] = [];
      let visualFields = this.visual().fields;

      let anySelection = false;

      for (let dataRow of this.getSelectedRows()) {
        let dataPointFields = dataRow.result.columns
          .map(c => ({
            field: visualFields.find(c.target),
            value: dataRow.values[c.dataIndex]
          }))
          .filter(c => c.field);

        if (!dataPointFields.length)
          continue;

        let dataPoint = this.dataPoints.get(dataPointFields);
        dataPoints.push(dataPoint);
        anySelection = true;
      }

      this.selectionSet(dataPoints);
      this.setNonDataPointSelectionActive(anySelection);
    });
  }

  private readonly updateDataPointSelectionFromAgGridDebounced = debounced(() => {
    this.updateDataPointSelectionFromAgGrid();
  }, 1, this.destroyRef);

  private updateDataPointsNextFrame() {
    setTimeout(()=> this.updateDataPointSelectionFromAgGridDebounced());
  }

  onAgGridSelectionChanged(e: SelectionChangedEvent) {
    this.updateDataPointsNextFrame();
  }

  onAgGridCellSelectionChanged(e: CellSelectionChangedEvent) {
    this.setNonDataPointSelectionActive(this.hasAnySelection());

    if (e.finished)
      this.updateDataPointsNextFrame();
  }

  onAgGridRowSelected(e: RowSelectedEvent) {
  }

  onAgGridFirstDataRendered(e: FirstDataRenderedEvent) {
    this.dataTable.ag.firstDataRenderedState.set(true);
    this.applyAutoSizingToAllColumns();
  }

  onAgGridCellKeyDown(e: CellKeyDownEvent) {
    let key = e.event as KeyboardEvent;
    if (!key.shiftKey)
      return;

    let arrowKey = key.key.startsWith('Arrow');
    if (!arrowKey)
      return;

    this.cellNavigationStarted.set(true);

    let direction = 
      key.key == 'ArrowUp'   ? -1 :
      key.key == 'ArrowDown' ? +1 : 0;

    if (direction == 0)
      return;

    let row = e.api.getDisplayedRowAtIndex(e.rowIndex + direction);
    if (!row)
      return;

    e.api.setNodesSelected({
      nodes: [row],
      newValue: true,
      source: 'api'
    });
  }

  @HostListener('window:click', ['$event'])
  @HostListener('window:touchstart', ['$event'])
  @HostListener('window:focus', ['$event'])
  endCellNavigation(e: Event) {
    this.cellNavigationStarted.set(false);
  }

  readonly agGridGetMainMenuItems = (params: GetMainMenuItemsParams, totals?: boolean) => {
    let items: (DefaultMenuItem | MenuItemDef)[] = [];

    let rowClick = 'node' in params && !!params.node;
    let headerClick = !!params.column;

    if (!rowClick && !headerClick)
      return [];

    if (rowClick) {
      if (this.hasSelection()) {
        if (!totals) {
          let node = (params as any).node as IRowNode;
          
          items.push({
            name: 'Copy',
            icon: '<i class="far fa-copy"></i>',
            action: _ => params.api.copyToClipboard(),
          });
      
          items.push({
            name: 'Copy with Headers',
            icon: '<i class="far fa-copy"></i>',
            action: _ => params.api.copyToClipboard({ includeGroupHeaders: true, includeHeaders: true }),
          });

          items.push('separator');

          if (false) { // node?.isSelected() disabled until post mvp
            items.push({
              name: 'Deselect This Row',
              action: _ => node.setSelected(false)
            });
          }
          items.push({
            name: 'Clear Selection',
            icon: '<i class="far fa-ban"></i>',
            action: _ => {
              this.selectionClear();
            }
          });
        }
      }
    }

    if (!rowClick) {
      if (!totals) {
        let column = params.column;
        if (column) {
          let field = column.getUserProvidedColDef()?.context as TableField;
          if (field) {
            if (items.length)
              items.push('separator');

            let sort = column.getSort();
            let fieldSorting = this.visual().fieldSorting();
            let isMultiSorting = fieldSorting.sortedFieldCount() > 1;

            if (sort != 'asc') {
              items.push({
                name: 'Sort Ascending',
                icon: '<i class="fas fa-arrow-up"></i>',
                action: params => {
                  this.updateFieldSort(field, { direction: SortDirection.Ascending, userSort: true, append: isMultiSorting });
                  this.broadcastStateChange();
                }
              });
            }
            
            if (sort != 'desc') {
              items.push({
                name: 'Sort Descending',
                icon: '<i class="fas fa-arrow-down"></i>',
                action: params => {
                  this.updateFieldSort(field, { direction: SortDirection.Descending, userSort: true, append: isMultiSorting });
                  this.broadcastStateChange();
                }
              });
            }

            if (sort) {
              let fieldSorting = this.visual().fieldSorting();
              if (fieldSorting.sortedFieldCount() == 1) {
                items.push({
                  name: 'Clear Sort',
                  action: params => {
                    this.removeFieldSort(field);
                    this.broadcastStateChange();
                  }
                });
              }
              else {
                if (field) {
                  items.push({
                    name: 'Clear this column sort',
                    action: params => {
                      this.removeFieldSort(field);
                      this.broadcastStateChange();
                    }
                  });
                }

                items.push({
                  name: 'Clear all sort',
                  action: params => {
                    this.clearFieldSort();
                    this.applyDefaultFieldSortIfNecessary();
                    this.broadcastStateChange();
                  }
                });
              }
            }
          }
        }
      }

      if (items.length) 
        items.push('separator');

      if (params.column) {
        items.push({
          name: 'Autosize This Column',
          action: params => {
            params.column.getColDef().context.userResized.set(true);
            params.api.autoSizeColumns({
              colIds: [params.column.getColId()],
              defaultMaxWidth: AgGridConstants.MAX_COLUMN_WIDTH,
              defaultMinWidth: AgGridConstants.MIN_COLUMN_WIDTH
            });
            this.captureAndSetColumnsState();
          }
        });
      }

      items.push({
        name: 'Autosize All Columns',
        action: params => {
          this.setAllColumnsUserResized();
          params.api.autoSizeAllColumns({
            defaultMaxWidth: AgGridConstants.MAX_COLUMN_WIDTH,
            defaultMinWidth: AgGridConstants.MIN_COLUMN_WIDTH
          });
          this.captureAndSetColumnsState();
        }
      });

      if (this.appContext.features()?.uI2.enableHideTotals) {
        if (params.column) {
          let field = params.column.getColDef().context as TableField;
          if (field.grandTotal.canShow()) {
            if (items.length)
              items.push('separator');

            let newTotalsHidden =
              isNullOrUndefined(field.totalsHiddenByUser())
                ? field.grandTotal.enabled()
                : !field.totalsHiddenByUser();

            items.push({
              name: newTotalsHidden ? 'Hide Totals' : 'Show Totals',
              action: params => {
                field.totalsHiddenByUser.set(newTotalsHidden);
                this.captureAndSetColumnsState();
              }
            });
          }
        }
      }
    }

    return items;
  };


  readonly agGridGetContextMenuItems = (params: GetContextMenuItemsParams, totals?: boolean) => {
    return this.agGridGetMainMenuItems(<any>params, totals);
  };
  
  readonly agGridGetMainMenuItems_Totals: GetMainMenuItems = 
    (params: GetMainMenuItemsParams) => this.agGridGetMainMenuItems(params, true);

  readonly agGridGetContextMenuItems_Totals: GetContextMenuItems = 
    (params: GetContextMenuItemsParams) => this.agGridGetContextMenuItems(params, true);


  agGridUpdateColumnSizingFromState() {
    let columns = this.state()?.columns() ?? [];
    this.setUserResizedFromState()

    this.agUpdateState((state, api) => {
      let columnSizingModel = [...(state.columnSizing?.columnSizingModel ?? [])];
      let max = Math.min(columns.length, columnSizingModel.length);
      let changed = false;

      for (let i = 0; i < max; ++i) {
        let model = columnSizingModel[i];
        let fieldRef = (api.getColumnDef(model.colId).context as TableField)?.vf?.referenceString();

        let column = columns.find(c => c.field == fieldRef || c.field == model.colId);
        if (column) {
          if (model.width == column.width && model.flex == column.flex) {
            continue;
          }

          columnSizingModel[i] = {
            ...model,
            ...column
          };

          changed = true;
        }
      }

      if (!changed)
        return state;

      return {
        ...state,

        columnSizing: {
          ...state.columnSizing,
          columnSizingModel
        }
      };
    });

    this.applyAutoSizingToAllColumns()
  }

  setUserResizedFromState() {
    let columnStates = this.state()?.columns();

    let columnDefs = this.dataTableAgGrid().api.getColumnDefs();
    for (let columnDef of columnDefs ?? []) {
      let field = columnDef.context as TableField;
      let fieldRef = field?.vf?.referenceString();

      let state = columnStates?.find(c => c.field == fieldRef);

      if (!state?.userResized)
        continue;

      field.userResized.set(true);
    }
  }

  applyAutoSizingToAllColumns() {
    let cols = this.dataTableAgGrid()?.api.getColumnDefs();
    let colsToAutoSize =
      cols?.map(c => c.context as TableField)
      .filter(c => !c.userResized() && !c.data.field.defaultWidth)

    let colIds = colsToAutoSize.map(c => c.data.ag.columnDef().colId);
    if (colIds?.length)
      this.dataTableAgGrid()?.api.autoSizeColumns({
        colIds: colIds,
        defaultMaxWidth: AgGridConstants.MAX_COLUMN_WIDTH,
        defaultMinWidth: AgGridConstants.MIN_COLUMN_WIDTH
      });
  }

  setAllColumnsUserResized() {
    for (let cols of this.dataTableAgGrid()?.api.getColumnDefs()) {
      let field = cols.context as TableField;
      field.userResized.set(true);
    }
  }

  agGridUpdateScrollFromState() {
    let scroll = this.state()?.scroll();
    this.agUpdateState(state => ({
      ...state,

      scroll: {
        ...state.scroll,
        ...scroll
      },
    }));
  }

  private agUpdateState(update: (x: GridState, api?: GridApi) => GridState) {
    let grids = [this.dataTableAgGrid(), this.grandTotalsTableAgGrid()];
    grids.forEach(g => {
      if (!g?.api || g.api.isDestroyed())
        return;

      let prevState = g.api.getState() ?? {};
      let state = update(prevState, g.api);
      if (prevState !== state)
        g.api.setState(state);
    });
  }

  readonly agGridGetNoItems = (params: any) => [];
  //#endregion
  
  readonly dataTable        = new DataTable(this);
  readonly grandTotalsTable = new GrandTotalsTable(this);
  
  readonly rendered = signal(false);
  readonly failed   = signal(false);


  private onRenderStart() {
    LOGGER.debug('onRenderStart', { component: this });

    this.rendered.set(false);
    this.failed.set(false);
    
    this.broadcastEvent({ 
      type: VisualHost.EventType.RenderStart
    });
  }

  private onRenderComplete() {
    let error = (this.getError() ?? this.data.contentError());
    let failed = !!error;

    LOGGER.debug('onRenderComplete', { component: this, failed, error });

    this.failed.set(failed);
    this.rendered.set(true);
    
    this.broadcastEvent({ 
      type: VisualHost.EventType.RenderComplete,

      failed,
      error
    });
  }

  private isRenderComplete() {
    if (this.data.contentError())
      return true;

    if (this.canRender()) {
      if (!this.dataTable.data.loaded())
        return false;

      if (!this.dataTable.data.success())
        return true;

      if (!this.dataTable.ag.firstDataRendered())
        return false;
        

      if (this.grandTotalsTable.enabled()) {
        if (this.grandTotalsTable.data.loaded()) {
          if (!this.grandTotalsTable.data.success())
            return true;

          if (this.grandTotalsTable.ag.firstDataRendered())
            return true;
        }
      }
      else {
        return true;
      }
    }

    return false;
  }

  private getError() {
    let dataError = this.dataTable.data.error();
    let grandTotalsError = this.grandTotalsTable.enabled() ? this.grandTotalsTable.data.error() : null;

    if (dataError && grandTotalsError)
      return new AggregateError([dataError, grandTotalsError]);

    return dataError ?? grandTotalsError;
  }

  constructor() {
    super();

    effect(() => {
      let wasRendered = untracked(() => this.rendered());
      let rendered = this.isRenderComplete();

      untracked(() => {
        if (rendered) {
          if (!wasRendered) {
            this.onRenderComplete();
          }
        }
        else {
          if (wasRendered) {
            this.onRenderStart();
          }
        }
      });
    });

    let sizingUpdate = debounced((updateState?: boolean) => {
      if (this.dataTable.ag.gridReady()) {
        this.agGridUpdateColumnSizingFromState();

        if (updateState) 
          this.updateState();
      }
    }, 50, this.destroyRef);

    effect(() => {
      let _ = [this.dataTable.ag.gridReady(), this.dataTable.ag.columnDefs()];
      untracked(() => sizingUpdate(true));
    });

    effect(() => {
      let _ = [this.state()?.columns()];
      untracked(() => sizingUpdate(false));
    });
    
    effect(() => {
      let _ = [this.state()?.columns(), this.dataTableAgGrid()];
      untracked(() => this.applyColumnState());
    });

    effect(() => {
      let [gridReady] = [this.dataTable.ag.gridReady(), this.dataTable.ag.columnDefs(), this.state()?.scroll()];
      untracked(() => {
        if (gridReady) {
          this.agGridUpdateScrollFromState();
        }
      });
    });
  }

  private applyColumnState() {
    let columnStates = this.state()?.columns();
    let columnDefs = this.dataTableAgGrid()?.api?.getColumnDefs();

    for (let columnDef of columnDefs ?? []) {
      let field = columnDef.context as TableField;
      let fieldRef = field?.vf?.referenceString();
      if (!fieldRef)
        continue;

      let state = columnStates?.find(c => c.field == fieldRef);
      field.totalsHiddenByUser.set(state?.totalsHidden);
    }
  }


  destroyed = false;
  ngOnDestroy() {
    this.dataTable.data.source()?.destroy();
    super.ngOnDestroy();
    this.destroyed = true;
  }

  protected override onBeforeSave(e: VisualHost.BeforeSaveEvent) {
    if (this.defaultSortApplied()) {
      this.defaultSortApplied.set(false);
      this.applySortFromAgGrid();
    }
  }

  protected override onFieldRenamed(e: VisualHost.FieldRenamedEvent) {

      let field = this.tableFields().find(f => f.vf.target == e.field.target);

      if (field.userResized())
        return;

    this.pendingFieldRenames.update(f => [...f, field]);
  }

  pendingFieldRenames = signal<TableField[]>([]);

  processFieldRename() {
    setTimeout(() => {
      if (this.destroyed)
        return;

      this.dataTableAgGrid().api.autoSizeColumns({
        colIds: this.pendingFieldRenames().map(f => f.data.ag.columnDef().colId),
        defaultMaxWidth: AgGridConstants.MAX_COLUMN_WIDTH,
        defaultMinWidth: AgGridConstants.MIN_COLUMN_WIDTH
      });

      this.pendingFieldRenames.set([]);

      this.captureAndSetColumnsState();
    });
  }

  agFieldsUpdated = effect(() => {
    let _ = this.dataTable.ag.columnDefs();

    if (this.pendingFieldRenames().length)
      this.processFieldRename();
  });

  private updateState() {
    this.captureState(this.state());
  }

  public captureState(state: AgGridTableVisual.State) {
    super.captureState(state);

    let grid = this.dataTableAgGrid();
    if (!grid?.api || grid.api.isDestroyed()) {
      LOGGER.error(`Cannot capture state because AgGrid is not bootstrapped.`, { component: this });
      return false;
    }

    state.columns.set(this.captureColumnsState());
    state.scroll.set(this.captureScrollState());
    return true;
  }

  private captureAndSetColumnsState() {
    this.state().columns.set(this.captureColumnsState());
    this.broadcastStateChange();
  }

  private captureColumnsState() {
    let grid = this.dataTableAgGrid();

    let columns = grid.api.getColumns().map(x => {
      let column = x.getDefinition();
      let field = column.context as TableField;
      return <AgGridConstants.VisualColumnState>{
        field: field?.vf?.referenceString() ?? x.getColId(),
        width: x.getActualWidth(),
        flex: x.getFlex(),
        userResized: field?.userResized() ?? false,
        totalsHidden: field?.totalsHiddenByUser()
      };
    });

    return columns;
  }
  
  private captureScrollState() {
    let grid = this.dataTableAgGrid();
    let gridState = grid.api.getState();

    return gridState.scroll;
  }


  protected override getClickEventData(e: MouseEvent): VisualHost.ClickEventData {
    let data = super.getClickEventData(e);

    let target = e.target as HTMLElement;
    if (target.closest('.ag-body-vertical-scroll') ||
      target.closest('.ag-body-horizontal-scroll')) {
      data = {
        ...data,
        resetCrossFilter: false
      };

      return data;
    }

    if (!target.closest('.ag-grid-table-data'))
      return data;

    if (target.closest('.ag-header-viewport')) {
      if (!target.closest('.ag-header-cell-menu-button'))
        return data;
    }
    else
    if (!target.closest('.ag-row'))
      return data;

    data = {
      ...data,
      action: VisualHost.ClickAction.Selection,
      resetCrossFilter: this.isOtherVisualCrossFilterSource()
    };

    return data;
  }
}

export namespace AgGridTableVisual {
  export const roles = VisualFieldRoleTypeMap.create({
    columns: new VisualFieldRoleType('columns', VisualFieldRoleType.Defaults.tableFields),
  });

  export class State extends Visual.State {
    @Serialize()
    readonly columns = signal<ReadonlyArray<AgGridConstants.VisualColumnState>>([]);

    @Serialize()
    readonly scroll = signal<ScrollState>(null);
  }

  export class Style extends VisualStyle {
    @Serialize(AgGridConstants.TableLayout.serializationOptions)
    readonly tableLayout = signal<AgGridConstants.TableLayout>(AgGridConstants.TableLayout.standard);
  }
  
  export interface Options extends Visual.Options {
  }

  export const type = new VisualType<Options>('agGridTable', AgGridTableVisualComponent, {
    fieldRoles: roles,
    className: 'agGridTable',
    visualOptions: {
    },
    canReuse: true,
    createState: o => new State(),
    captureState: (state, { host }) => host?.component()?.captureState(state),
    createStyle: o => new Style(),
    styleEditor: AgGridTableStyleEditorComponent,
    fieldSorting: VisualFieldSorting.any(),
    includeSortingInQuery: false,
    loadedWhenRendering: true,
    disableLayoutSorting: true,
    showRowCount: true,
    hasBackground: false,
    ignoreFieldRoleDisplayOrder: true,
  });
}

class DataTable {
  constructor(readonly component: AgGridTableVisualComponent) { }
  
  get canRender()   { return this.component.canRender; }
  get tableFields() { return this.component.tableFields; }

  readonly data = new class {
    constructor(private readonly table: DataTable) { 
      this.updateSource();
    }

    private get component()   { return this.table.component; }
    private get tableFields() { return this.table.tableFields; }

    readonly loaded    = computed(() => {
      let query = this.component.data.query();
      return query && query.completed();
    });
    
    readonly success   = computed(() => this.loaded() && this.component.data.success());

    readonly error     = computed(() => this.table.ag.error() ?? this.component.data.query()?.error());
    readonly orderBy   = computed(() => this.tableFields().map(x => x.orderBy()).filter(x => !!x));
    
    readonly source    = signal<AgGridTableVisualDataSource>(null);

    updateSource() {
      untracked(() => this.source()?.destroy());

      this.source.set(new AgGridTableVisualDataSource(this.table.component));
    }
  }(this);

  readonly ag = new class {
    constructor(private readonly table: DataTable) { }

    private get component() { return this.table.component; }
    private get tableFields() { return this.table.tableFields; }
    private get data() { return this.table.data; }

    get grid() { return this.component.dataTableAgGrid; }

    readonly options = computed<GridOptions>(() => ({
      ...AgGridConstants.data.ag.options,
      defaultColDef: {
        ...AgGridConstants.data.ag.options.defaultColDef
      },
      getRowHeight: this.getRowHeight,
    }));

    readonly theme = computed(() => {
      let layout = this.component.tableLayout();

      return AgGridConstants.data.ag.theme.withParams({
        headerHeight: layout.headerHeightPx,
        rowHeight: layout.rowHeightPx,
        fontSize: layout.fontSize,
      });
    });

    readonly themeChanged = effect(() => {
      let theme = this.theme();
      untracked(() => {
        let grid = this.grid();
        if (grid?.api && !grid.api.isDestroyed()) {
          grid.api.resetRowHeights();
        }
      });
    });
    
    readonly getRowHeight = () => this.component.tableLayout().config.rowHeight;


    readonly columnDefs = computed(() => this.component.orderedTableFields().map(x => x.data.ag.columnDef()));
  
    readonly alignedGrids = computed(() => {
      let [enabled, grid] = [this.component.grandTotalsTable.enabled(), this.component.grandTotalsTable.ag.grid()];
      return enabled && grid ? [grid] : [];
    });
    
    readonly error      = signal<AppError>(null);

    readonly gridReadyState         = signal(false);
    readonly firstDataRenderedState = signal(false);
    readonly gridReady         = computed(() => !!this.grid()?.api && !this.grid().api.isDestroyed() && this.gridReadyState());
    readonly firstDataRendered = computed(() => this.gridReady() && this.firstDataRenderedState());

    onGridReset() {
        this.grid()?.ngOnDestroy();
        this.firstDataRenderedState.set(false);
        this.gridReadyState.set(false);
    }

    readonly getRowId = (x: any) => `${x.data.id}`;
  }(this);
}

class GrandTotalsTable {
  constructor(readonly component: AgGridTableVisualComponent) { }
  
  get canRender()   { return this.component.canRender; }
  get tableFields() { return this.component.tableFields; }
  
  readonly enabled = computed(() => this.tableFields().some(x => x.grandTotal.canShow()));
  readonly hidden = computed(() => !this.tableFields().some(x => x.grandTotal.enabled()));

  readonly data = new class {
    constructor(private readonly table: GrandTotalsTable) { }

    private get component()   { return this.table.component; }
    private get tableFields() { return this.table.tableFields; }

    readonly resource = computed(() => {
      let fields = this.tableFields().filter(x => x.grandTotal.canShow());
      if (!fields.length)
        return null;
    
      return this.component.data.aggregateColumns({
        abortSignal: this.component.dataTable.data.source()?.serverSideDatasourceAdapter()?.destroyed$,
        aggregations: fields.map(f => ({
          target: f.vf,
          aggregation: f.grandTotal.aggregation(),
        }))
      });
    });

    readonly loaded    = computed(() => {
      let res = this.resource();
      return res && res.completed();
    });

    readonly success   = computed(() => this.loaded() && this.resource().success());

    readonly result    = computed(() => this.loaded() ? this.resource().result() : null);

    readonly error     = computed(() => this.resource()?.failure() ? this.resource()?.error() : null);
  }(this);
  
  readonly ag = new class {
    constructor(private readonly table: GrandTotalsTable) { }

    private get component()   { return this.table.component; }
    private get tableFields() { return this.table.tableFields; }
    private get data()        { return this.table.data; }

    get grid() { return this.component.grandTotalsTableAgGrid; }
    
    readonly options = computed<GridOptions>(() => ({
      ...AgGridConstants.grandTotals.ag.options,
      getRowHeight: this.getRowHeight,
    }));

    readonly theme = computed(() => {
      let layout = this.component.tableLayout();

      return AgGridConstants.grandTotals.ag.theme.withParams({
        rowHeight: layout.grandTotalRowHeightPx,
        fontSize: layout.fontSize,
      });
    });
    
    readonly themeChanged = effect(() => {
      let theme = this.theme();
      untracked(() => {
        let grid = this.grid();
        if (grid?.api && !grid.api.isDestroyed()) {
          grid.api.resetRowHeights();
        }
      });
    });
    
    readonly getRowHeight = () => this.component.tableLayout().config.grandTotalRowHeight;


    readonly columnDefs = computed(() => 
      [...this.tableFields()]
        .sort((a, b) => a.index() - b.index())
        .map(x => x.grandTotal.ag.columnDef()));
  
    readonly alignedGrids = computed(() => {
      let [canRender, grid] = [this.table.canRender(), this.component.dataTable.ag.grid()];
      return canRender && grid ? [grid] : [];
    });

    readonly rowData = computed<any>(() => {
      if (!this.data.loaded()) {
        return this.tableFields().map(_ => '');
      }

      return [this.data.resource().result()];
    });
    
    readonly gridReadyState         = signal(false);
    readonly firstDataRenderedState = signal(false);
    readonly gridReady         = computed(() => !!this.grid()?.api && !this.grid().api.isDestroyed() && this.gridReadyState());
    readonly firstDataRendered = computed(() => this.gridReady() && this.firstDataRenderedState());

    onGridReset() {
      this.grid()?.ngOnDestroy();
      this.firstDataRenderedState.set(false);
      this.gridReadyState.set(false);
    };

  }(this);
}

const TableFieldValueGetter = <ValueGetterFunc<AgGridTableVisualDataRow>>(params => {
  return untracked((): AgGridConstants.AgGridCellData | string => {
    if (!params.data)
      return '';

    let field = params.colDef.context as TableField;
    if (!field)
      return '';
      
    let row = params.data;
    let formatter = field.component.formatter(row.result);
    let value = formatter.getRowValueData(row.values, field.vf);
    return AgGridConstants.AgGridCellData.create(field.component, value, field.vf);
  });
});

const TableFieldGrandTotalValueGetter = <ValueGetterFunc<any>>(params => {
  return untracked((): AgGridConstants.AgGridCellData | string => {
    let field = params.colDef.context;
    if (!field)
      return '';

    if (!params.data || !field.grandTotal.canShow() || !field.grandTotal.enabled()) {
      if (field.index() == 0)
        return 'Total';

      return '';
    }
    
    let rowsResult = <RowsResult>params.data;
    let formatter = field.component.formatter(rowsResult);
    let value = formatter.getRowValueData(rowsResult.rows[0], field.vf);
    return AgGridConstants.AgGridCellData.create(field.component, value, field.vf);
  });
});

export class TableField {
  readonly index: WritableSignal<number>;

  constructor(
    readonly component: AgGridTableVisualComponent,
    readonly vf: VisualField,
    readonly requestIndex: number,
    index: number) {
    this.index = signal(index);
  }

  readonly id = `${this.requestIndex}_${this.vf.target.referenceString()}`;
  readonly defaultWidth = AgGridConstants.getDefaultColumnWidth(this.vf);

  get shortTitle()   { return this.vf.shortTitle; }
  get longTitle()    { return this.vf.shortTitle; }
  get isAggregated() { return this.vf.isAggregated; }
  get dataType()     { return this.vf.dataType; }

  readonly userResized = signal(false);
  readonly totalsHiddenByUser = signal(null);

  readonly data = new class {
    constructor(readonly field: TableField) { }

    readonly ag = {
      columnDef: computed<ColDef>(() => {
        let cellClass = ['ag-grid-data-cell'];

        if (this.field.vf.dataType.isNumeric)
          cellClass.push('ag-grid-cell-right-align');

        let orderBy = this.field.orderBy();
        return {
          ...AgGridConstants.data.ag.columnDef,

          colId: this.field.id,

          context: this.field,
          valueGetter: TableFieldValueGetter,

          cellClass:     cellClass,
          headerClass:   cellClass,
          headerName:    this.field.shortTitle,
          headerTooltip: this.field.shortTitle,

          sort:
            orderBy?.sortDirection == SortDirection.Ascending  ? 'asc' :
            orderBy?.sortDirection == SortDirection.Descending ? 'desc' : <any>false,

          sortIndex: orderBy?.sortOrder,

          initialWidth: this.field.defaultWidth,

          sortingOrder: this.field.vf.defaultSortDirection() != SortDirection.Descending ?
            ['asc', 'desc', null] :
            ['desc', 'asc', null], //IN8471 Avoid infinite sorting loop since Desc would go to null which would default to Desc

          suppressHeaderKeyboardEvent: () => true,
          suppressKeyboardEvent: (event) => {
            if (event.event.key == "ArrowUp" && event.node.rowIndex <= 0)
              return true
          },
        };
      }),
    };
  }(this);

  readonly grandTotal = new class {
    constructor(readonly field: TableField) { }

    readonly aggregation = computed(() => DataAggregation.Sum);
    readonly dataType    = computed(() => this.aggregation().getResultDataType(this.field.vf.dataType));

    readonly enabled = computed(() => {
      if (!this.canShow())
        return false;

      let canHideTotals = this.field.component.appContext.features()?.uI2.enableHideTotals;
      if (!canHideTotals)
        return !this.field.vf.totalsHidden;

      let totalsHiddenByUser = this.field.totalsHiddenByUser();
      if (isNullOrUndefined(totalsHiddenByUser))
        return !this.field.vf.totalsHidden;

      return !totalsHiddenByUser;
    });

    readonly canShow = computed(() => {
      if (this.field.vf.isAggregated) {
        if (this.field.vf.dataType.isNumeric)
          return true;

        if ([DataAggregation.Count, DataAggregation.CountDistinct].includes(this.aggregation()))
          return true;
      }

      return false;
    });

    readonly ag = {
      columnDef: computed<ColDef>(() => {
        let cellClass = [
          'ag-grid-grand-totals-cell',
          `ag-grid-cell-data-type-${this.dataType.name}`
        ];

        if (this.field.vf.dataType.isNumeric)
          cellClass.push('ag-grid-cell-right-align');

        if (!this.field.grandTotal.enabled()) {
          if (this.canShow())
            cellClass.push('ag-grid-cell-hidden');
        }

        return {
          ...this.field.data.ag.columnDef(),

          ...AgGridConstants.grandTotals.ag.columnDef,

          context: this.field,
          valueGetter: TableFieldGrandTotalValueGetter,

          cellClass,
          headerClass: cellClass,
          headerTooltip: this.aggregation().formatAggregationOfText(this.field.vf.shortTitle),
        };
      }),
    };
  }(this);


  readonly orderBy = computed<VisualDataAdapter.OrderBy>(() => {
    let [sorted, sortOrder, sortDirection] = [
      this.vf.sorted(),
      this.vf.sortOrder(),
      this.vf.sortDirection()
    ];

    if (!sorted || !sortDirection)
      return null;

    return {
      target: this.vf.target,
      sortOrder,
      sortDirection,
    };
  });
}
